<template>
  <div class="page-content-container">
    <h1 class="page-title">交易凭证参考示例</h1>
    
    <el-card class="content-card" v-loading="loading">
      <div class="card-header">
        <el-button type="primary" @click="saveContent" :loading="saving">
          <el-icon><Check /></el-icon>保存内容
        </el-button>
      </div>
      
      <div class="card-content">
        <el-form label-width="80px">
          <el-form-item label="标题">
            <el-input v-model="formData.title" placeholder="请输入页面标题" />
          </el-form-item>
          
          <el-form-item label="内容">
            <!-- 图片大小提示 -->
            <el-alert
              title="图片上传提示"
              type="info"
              :closable="false"
              show-icon
              style="margin-bottom: 16px;"
            >
              <template #default>
                <div>基于当前服务器配置，建议您将上传的图片大小控制在200KB以下，以确保页面加载速度和编辑体验。</div>
              </template>
            </el-alert>
            
            <!-- 使用富文本编辑器 -->
            <div class="editor-container">
              <Toolbar
                style="border-bottom: 1px solid #ccc"
                :editor="editorRef"
                :defaultConfig="toolbarConfig"
                :mode="mode"
              />
              <Editor
                style="height: 500px; overflow-y: hidden;"
                v-model="formData.content"
                :defaultConfig="editorConfig"
                :mode="mode"
                @onCreated="onCreated"
              />
            </div>
          </el-form-item>
        </el-form>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { Check } from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';
import request from '../../api/axios';
import { Editor, Toolbar } from '@wangeditor/editor-for-vue';
import '@wangeditor/editor/dist/css/style.css';

const saving = ref(false);
const loading = ref(true);
const formData = ref({
  title: '交易凭证参考示例',
  slug: 'transaction-proof-guide',
  content: ''
});

// 富文本编辑器配置
const editorRef = ref(null);
const mode = ref('default');
const toolbarConfig = ref({
  excludeKeys: [
    'fullScreen',
    'preview',
    'print',
    'codeBlock',
    'splitScreen',
    'blockquote'
  ]
});
const editorConfig = ref({
  placeholder: '请输入页面内容',
  MENU_CONF: {
    uploadImage: {
      server: '/manage/upload/image',
      fieldName: 'file',
      maxFileSize: 2 * 1024 * 1024, // 2M
      allowedFileTypes: ['image/*'],
      // 自定义上传图片
      customUpload: async (file, insertFn) => {
        try {
          const formData = new FormData();
          formData.append('file', file);
          
          // 获取API基础URL
          const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
          const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
          const baseDomain = domainMatch ? domainMatch[1] : window.location.origin;
          
          const response = await request({
            url: '/manage/upload/image',
            method: 'post',
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          if (response.errno === 0 && response.data && response.data.length > 0) {
            let imgUrl = response.data[0].url;
            // 确保返回的是完整的绝对路径
            if (imgUrl && !imgUrl.startsWith('http://') && !imgUrl.startsWith('https://')) {
              imgUrl = `${baseDomain}${imgUrl}`;
            }
            insertFn(imgUrl); // 插入图片
          } else {
            ElMessage.error('图片上传失败');
          }
        } catch (error) {
          console.error('图片上传失败:', error);
          ElMessage.error('图片上传失败');
        }
      }
    }
  }
});

// 编辑器创建完成时的回调
const onCreated = (editor) => {
  editorRef.value = editor;
};

// 获取页面内容
const getPageContent = async () => {
  loading.value = true;
  try {
    const response = await request({
      url: `/manage/page-contents/${formData.value.slug}`,
      method: 'get'
    });
    
    if (response.code === 200) {
      formData.value = response.data;
    }
  } catch (error) {
    ElMessage.error('获取页面内容失败');
    console.error('获取页面内容失败:', error);
  } finally {
    loading.value = false;
  }
};

// 保存页面内容
const saveContent = async () => {
  try {
    saving.value = true;
    
    const response = await request({
      url: '/manage/page-contents',
      method: 'post',
      data: formData.value
    });
    
    if (response.code === 200) {
      ElMessage.success('保存成功');
    }
  } catch (error) {
    ElMessage.error('保存失败');
    console.error('保存页面内容失败:', error);
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  getPageContent();
});

// 组件销毁时，也销毁编辑器
onBeforeUnmount(() => {
  if (editorRef.value) {
    editorRef.value.destroy();
  }
});
</script>

<style scoped>
.page-content-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.content-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.card-content {
  padding: 20px 0;
}

.editor-container {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
}
</style>